import { RefObject, useEffect, useRef } from 'react';
import { pointerEngine, PlayGesture } from './pointerEngine';
import { playLoop } from './playLoop';
import { PRESETS, stepSpring, settled } from './springs';
import { RectCache, nearRect } from './rectCache';
import { magnetPush, flickSpeed, MAGNET_R } from './magnet';

export { magnetPush, MAGNET_R, MAGNET_MAX } from './magnet';

// Magnetic letters: every [data-letter] span inside the container is pushed away from the
// pointer and springs home (bouncy). A flick across the mark scatters the letters; a tap on
// it calls onTap. Transforms are written straight to the spans from playLoop ticks — no
// React state per frame. Letters are decorative spans, never typed text.
const FLICK_PAD = 40;
const WRITE_EPS = 0.1;
const FLICK_SPREAD = 0.7; // radians of random spread around the flick direction

type Letters = {
  els: HTMLElement[]; cx: number[]; cy: number[];
  x: { x: number; v: number }[]; y: { x: number; v: number }[]; wx: number[]; wy: number[];
};

export type MagnetApi = { impulse: (px: number) => void };

const live = new Set<() => { text: string; dx: number; dy: number }[]>();
type DebugHost = { __playDebug?: { letters?: () => { text: string; dx: number; dy: number }[] } };
function exposeDebug() {
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return;
  const dbg = (window as unknown as DebugHost).__playDebug;
  if (dbg && !dbg.letters) dbg.letters = () => Array.from(live).flatMap((f) => f());
}

function emptyLetters(): Letters { return { els: [], cx: [], cy: [], x: [], y: [], wx: [], wy: [] }; }

function collect(root: HTMLElement, L: Letters): void {
  const found = Array.from(root.querySelectorAll<HTMLElement>('[data-letter]'));
  if (found.length === L.els.length && found.every((e, i) => e === L.els[i])) return;
  L.els.forEach((e) => { if (!found.includes(e)) e.style.transform = ''; });
  L.els = found;
  L.cx = found.map(() => 0); L.cy = found.map(() => 0);
  L.x = found.map(() => ({ x: 0, v: 0 })); L.y = found.map(() => ({ x: 0, v: 0 }));
  L.wx = found.map(() => 0); L.wy = found.map(() => 0);
}

// Centers are measured from the rects minus our own current offset, i.e. the home position.
function measureLetters(L: Letters): void {
  for (let i = 0; i < L.els.length; i++) {
    const r = L.els[i].getBoundingClientRect();
    L.cx[i] = r.left + r.width / 2 - L.wx[i];
    L.cy[i] = r.top + r.height / 2 - L.wy[i];
  }
}

function writeLetter(L: Letters, i: number): void {
  const x = L.x[i].x, y = L.y[i].x;
  if (Math.abs(x - L.wx[i]) <= WRITE_EPS && Math.abs(y - L.wy[i]) <= WRITE_EPS) return;
  const home = Math.abs(x) <= WRITE_EPS && Math.abs(y) <= WRITE_EPS;
  L.wx[i] = home ? 0 : x; L.wy[i] = home ? 0 : y;
  L.els[i].style.transform = home ? '' : `translate3d(${x.toFixed(2)}px,${y.toFixed(2)}px,0)`;
}

export function useMagneticLetters(
  containerRef: RefObject<HTMLElement>,
  opts: { reduced: boolean; onTap?: (api: MagnetApi) => void },
): RefObject<MagnetApi> {
  const { reduced } = opts;
  const onTapRef = useRef(opts.onTap);
  onTapRef.current = opts.onTap;
  const api = useRef<MagnetApi>({ impulse: () => {} });

  useEffect(() => {
    const root = containerRef.current;
    if (!root || reduced) return;
    const L = emptyLetters();
    const box = new RectCache(() => containerRef.current);
    let lastLetters = -1e9;
    let unreg: (() => void) | null = null;
    let moved = false;

    const measure = (now: number, soon: boolean) => {
      const prev = box.rect;
      const r = box.get(now, soon);
      if (r !== prev || now - lastLetters >= 250) { collect(root, L); measureLetters(L); lastLetters = now; }
      return r;
    };
    const pointerNear = (r: DOMRect | null) => {
      const p = pointerEngine.state;
      if (!r || (p.type !== 'mouse' && !p.down)) return false;
      return nearRect(r, p.x, p.y, MAGNET_R);
    };
    const tick = (dt: number) => {
      const now = performance.now();
      const r = measure(now, moved);
      moved = false;
      const near = pointerNear(r);
      const p = pointerEngine.state;
      let busy = near;
      for (let i = 0; i < L.els.length; i++) {
        const t = near ? magnetPush(L.cx[i], L.cy[i], p.x, p.y) : { dx: 0, dy: 0 };
        L.x[i] = stepSpring(L.x[i], t.dx, dt, PRESETS.bouncy);
        L.y[i] = stepSpring(L.y[i], t.dy, dt, PRESETS.bouncy);
        if (!settled(L.x[i], t.dx) || !settled(L.y[i], t.dy)) busy = true;
        else { L.x[i] = { x: t.dx, v: 0 }; L.y[i] = { x: t.dy, v: 0 }; }
        writeLetter(L, i);
      }
      if (!busy && unreg) { unreg(); unreg = null; }
    };
    const wake = () => { if (!unreg) unreg = playLoop.register(tick); };
    const kickAll = (vx: number, vy: number, speed: number, spread: number) => {
      collect(root, L);
      const base = Math.atan2(vy, vx);
      for (let i = 0; i < L.els.length; i++) {
        const a = base + (Math.random() - 0.5) * spread;
        const k = speed * (0.75 + Math.random() * 0.25);
        L.x[i] = { x: L.x[i].x, v: L.x[i].v + Math.cos(a) * k };
        L.y[i] = { x: L.y[i].x, v: L.y[i].v + Math.sin(a) * k };
      }
      wake();
    };
    api.current = { impulse: (px: number) => kickAll(1, 0, flickSpeed(px), Math.PI * 2) };

    const near = (pad: number) => {
      const r = box.get(performance.now(), true);
      const p = pointerEngine.state;
      return !!r && nearRect(r, p.x, p.y, pad);
    };
    const onMove = () => {
      moved = true;
      if (pointerEngine.state.type !== 'mouse' && !pointerEngine.state.down) return;
      if (unreg || near(MAGNET_R)) wake();
    };
    const onFlick = (e: PlayGesture) => {
      if (!near(FLICK_PAD)) return;
      const sp = Math.hypot(e.vx, e.vy) || 1;
      kickAll(e.vx / sp, e.vy / sp, flickSpeed(), FLICK_SPREAD);
    };
    const onTap = () => { if (onTapRef.current && near(0)) onTapRef.current(api.current); };
    const onScroll = () => { if (pointerEngine.state.type === 'mouse' || pointerEngine.state.down) wake(); };
    const offs = [
      pointerEngine.on('move', onMove), pointerEngine.on('down', onMove), pointerEngine.on('up', wake),
      pointerEngine.on('flick', onFlick), pointerEngine.on('tap', onTap),
    ];
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    const dbg = () => L.els.map((e, i) => ({ text: e.textContent ?? '', dx: L.wx[i], dy: L.wy[i] }));
    live.add(dbg); exposeDebug();
    collect(root, L); measureLetters(L);
    return () => {
      offs.forEach((o) => o());
      window.removeEventListener('scroll', onScroll, { capture: true });
      if (unreg) unreg();
      live.delete(dbg);
      L.els.forEach((e) => { e.style.transform = ''; });
      api.current = { impulse: () => {} };
    };
  }, [containerRef, reduced]);

  return api;
}
